import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
} from "@comai-portal/design-system";
import { getPlatform, PLATFORMS } from "../content/platforms";

/** PlatformDetailPage — rebuilt on the design system. (CO 069) */
export function PlatformDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const platform = slug ? getPlatform(slug) : undefined;

  if (!platform) {
    return (
      <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
        <Section>
          <Container width="prose" className="text-center">
            <h1 className="text-display-md mb-3">Platform not found</h1>
            <p className="text-mute dark:text-mute-soft mb-8">
              That platform isn't part of ComOS — yet.
            </p>
            <Link
              to="/platforms"
              className={buttonStyles({ variant: "primary" })}
            >
              See all platforms
            </Link>
          </Container>
        </Section>
      </div>
    );
  }

  const others = PLATFORMS.filter((p) => p.slug !== platform.slug);

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container>
          <Link
            to="/platforms"
            className="text-sm text-mute dark:text-mute-soft hover:text-signal dark:hover:text-signal-bright"
          >
            ← All platforms
          </Link>
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="mt-6 max-w-prose"
          >
            <motion.p variants={fadeUp} className="ds-eyebrow mb-4">
              Platform
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-display-md text-balance"
            >
              {platform.name}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-signal dark:text-signal-bright mt-3"
            >
              {platform.tagline}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="text-mute dark:text-mute-soft mt-5"
            >
              {platform.blurb}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container>
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <p className="ds-eyebrow mb-4">What it does</p>
              <ul className="space-y-2.5">
                {platform.capabilities.map((c) => (
                  <li
                    key={c}
                    className="flex gap-2.5 text-ink/90 dark:text-paper/85"
                  >
                    <span className="text-signal dark:text-signal-bright mt-0.5">
                      ▸
                    </span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              {platform.agentCall ? (
                <>
                  <p className="ds-eyebrow mb-4">Agent-callable</p>
                  <Card className="bg-ink text-paper dark:bg-black font-mono text-sm overflow-x-auto mb-5">
                    {platform.agentCall}
                  </Card>
                </>
              ) : (
                <>
                  <p className="ds-eyebrow mb-4">Status</p>
                  <Card className="mb-5">
                    <span className="inline-block rounded-pill border border-mute-line dark:border-ink-line px-3 py-1 text-sm uppercase tracking-wide text-mute dark:text-mute-soft">
                      Coming soon
                    </span>
                    <p className="text-mute dark:text-mute-soft text-sm mt-3">
                      This platform is named and on the roadmap. Its agent
                      surface and human controls land in a future release — the
                      moment they do, it's composable on the same tenant, same
                      Coms.
                    </p>
                  </Card>
                </>
              )}
              {platform.replaces && (
                <>
                  <p className="ds-eyebrow mb-3">Replaces</p>
                  <div className="flex flex-wrap gap-2">
                    {platform.replaces.map((r) => (
                      <span
                        key={r}
                        className="rounded-pill border border-mute-line dark:border-ink-line px-3 py-1 text-sm text-mute dark:text-mute-soft"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHead
            eyebrow="Compose the rest"
            title="Nothing to enroll in. Pay as you go."
            lead="Most platforms are just interfaces your agents call — no signup, no per-platform plan. The few that hold your data get a tenant record the moment you first use one. Either way the only cost is a few Coms per action, all on this tenant's identity and balance."
          />
          <motion.div
            key={platform.slug}
            initial="hidden"
            animate="show"
            variants={stagger}
            className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {others.map((p) => (
              <motion.div key={p.slug} variants={fadeUp}>
                <Link to={`/platforms/${p.slug}`} className="block h-full">
                  <Card hover className="h-full">
                    <h3 className="text-display-sm mb-1">{p.name}</h3>
                    <p className="text-sm text-mute dark:text-mute-soft">
                      {p.tagline}
                    </p>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-10 text-center">
            <Link
              to="/start"
              className={buttonStyles({ variant: "primary", size: "lg" })}
            >
              Start building — free
            </Link>
          </div>
        </Container>
      </Section>
    </div>
  );
}
